import React, { useState, useEffect } from 'react';
import { recordView } from '../../components/layout/MultiApplyUpsell';
import { SendCVButton } from '../../components/agencies/SendCVButton';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import { agencyAPI, reviewsAPI, userAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
    Building2,
    MapPin,
    Phone,
    Mail,
    Globe,
    Star,
    ArrowLeft,
    Clock,
    Briefcase,
    Users,
    MessageSquare,
    Loader2,
    Lock,
    Send
} from 'lucide-react';

const AgencyDetailPage = () => {
    const { agencyId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user, login } = useAuth();
    const [agency, setAgency] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ average_rating: 0, review_count: 0 });
    const [loading, setLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    const [submittingReview, setSubmittingReview] = useState(false);
    const [reviewForm, setReviewForm] = useState({
        rating: 5,
        title: '',
        content: '',
    });

    useEffect(() => {
        fetchAgency();
        fetchReviews();
        recordView(`agency:${agencyId}`);
        // Track agency view for dashboard
        if (user) {
            userAPI.trackView('agency', agencyId).catch(() => {});
        }
    }, [agencyId]);

    const fetchAgency = async () => {
        try {
            const response = await agencyAPI.getById(agencyId);
            // Defensive: legacy/imported records may have list fields stored as
            // comma-separated strings or null. Normalise so .map/.slice never crash.
            const raw = response.data.agency || {};
            const LIST_FIELDS = ['industry', 'job_types', 'job_roles', 'experience_levels', 'work_modes', 'operating_cities'];
            const normalised = { ...raw };
            LIST_FIELDS.forEach((f) => {
                const v = normalised[f];
                if (typeof v === 'string') {
                    normalised[f] = v.split(',').map((s) => s.trim()).filter(Boolean);
                } else if (!Array.isArray(v)) {
                    normalised[f] = [];
                }
            });
            setAgency(normalised);
            // Use has_access from API response (checks admin/super_admin roles + subscription)
            setHasAccess(response.data.has_access);
        } catch (error) {
            console.error('Failed to fetch agency:', error);
            toast.error('Agency not found');
            navigate('/agencies');
        } finally {
            setLoading(false);
        }
    };

    const fetchReviews = async () => {
        try {
            const response = await reviewsAPI.getAgencyReviews(agencyId, { limit: 20 });
            setReviews(response.data.reviews);
            setReviewStats({
                average_rating: response.data.average_rating,
                review_count: response.data.review_count
            });
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        }
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            login();
            return;
        }

        if (!reviewForm.title.trim() || !reviewForm.content.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        setSubmittingReview(true);
        try {
            await reviewsAPI.create({
                agency_id: agencyId,
                rating: reviewForm.rating,
                title: reviewForm.title,
                content: reviewForm.content,
            });
            toast.success('Review submitted successfully!');
            setShowReviewDialog(false);
            setReviewForm({ rating: 5, title: '', content: '' });
            fetchReviews();
            fetchAgency();
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to submit review';
            toast.error(message);
        } finally {
            setSubmittingReview(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            </Layout>
        );
    }

    if (!agency) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <p className="text-neutral-500">Agency not found</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-neutral-50">
                {/* Header */}
                <div className="bg-white border-b">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <Link to="/agencies" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4">
                            <ArrowLeft className="w-4 h-4" />
                            Back to Agencies
                        </Link>
                        
                        <div className="flex flex-col md:flex-row gap-6 items-start">
                            <div className="w-20 h-20 rounded-2xl bg-primary-100 flex items-center justify-center shrink-0">
                                <Building2 className="w-10 h-10 text-primary-600" />
                            </div>
                            <div className="flex-1">
                                <h1 className={`text-3xl font-bold text-neutral-900 ${!hasAccess ? 'blur-sm select-none' : ''}`}>
                                    {agency.name}
                                </h1>
                                
                                {/* Rating */}
                                <div className="flex items-center gap-3 mt-2">
                                    <div className="flex">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                                key={star}
                                                className={`w-5 h-5 ${
                                                    star <= Math.round(reviewStats.average_rating)
                                                        ? 'text-amber-400 fill-amber-400'
                                                        : 'text-neutral-300'
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-neutral-600">
                                        {reviewStats.average_rating > 0 
                                            ? `${reviewStats.average_rating} (${reviewStats.review_count} reviews)`
                                            : 'No reviews yet'
                                        }
                                    </span>
                                </div>

                                <div className={`flex items-center gap-2 text-neutral-500 mt-2 ${!hasAccess ? 'blur-sm select-none' : ''}`}>
                                    <MapPin className="w-4 h-4" />
                                    <span>{agency.address}, {agency.city}, {agency.postcode}</span>
                                </div>

                                {/* Industries */}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {agency.industry?.map((ind) => (
                                        <Badge key={ind} variant="secondary">{ind}</Badge>
                                    ))}
                                </div>
                            </div>

                            {/* CTA */}
                            {hasAccess ? (
                                <Button 
                                    onClick={() => setShowReviewDialog(true)}
                                    className="bg-primary-600 hover:bg-primary-700"
                                    data-testid="write-review-btn"
                                >
                                    <Star className="w-4 h-4 mr-2" />
                                    Write a Review
                                </Button>
                            ) : (
                                <Button asChild>
                                    <Link to="/login" data-testid="unlock-details-signup-btn">
                                        <Lock className="w-4 h-4 mr-2" />
                                        Sign Up Free to Unlock
                                    </Link>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Description */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>About</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className={`text-neutral-600 ${!hasAccess ? 'blur-sm select-none' : ''}`}>
                                        {agency.description || 'No description available.'}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Job Types & Roles */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Specializations</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {agency.job_types?.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-neutral-500 mb-2">Job Types</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {agency.job_types.map((type) => (
                                                    <Badge key={type} variant="outline">{type}</Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {agency.job_roles?.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-neutral-500 mb-2">Job Roles</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {agency.job_roles.slice(0, 10).map((role) => (
                                                    <Badge key={role} variant="secondary" className="text-xs">{role}</Badge>
                                                ))}
                                                {agency.job_roles.length > 10 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        +{agency.job_roles.length - 10} more
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Reviews Section */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center justify-between">
                                        <span>Reviews ({reviewStats.review_count})</span>
                                        {hasAccess && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => setShowReviewDialog(true)}
                                            >
                                                Write a Review
                                            </Button>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {reviews.length === 0 ? (
                                        <div className="text-center py-8">
                                            <MessageSquare className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                                            <p className="text-neutral-500">No reviews yet. Be the first to review!</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            {reviews.map((review) => (
                                                <div key={review.review_id} className="border-b last:border-0 pb-6 last:pb-0">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                                    <span className="text-sm font-medium text-primary-600">
                                                                        {review.user_name?.[0]?.toUpperCase() || '?'}
                                                                    </span>
                                                                </div>
                                                                <span className="font-medium text-neutral-900">{review.user_name}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <div className="flex">
                                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                                        <Star
                                                                            key={star}
                                                                            className={`w-4 h-4 ${
                                                                                star <= review.rating
                                                                                    ? 'text-amber-400 fill-amber-400'
                                                                                    : 'text-neutral-300'
                                                                            }`}
                                                                        />
                                                                    ))}
                                                                </div>
                                                                <span className="text-sm text-neutral-400">
                                                                    {new Date(review.created_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <h4 className="font-medium text-neutral-900 mt-3">{review.title}</h4>
                                                    <p className="text-neutral-600 mt-1">{review.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Contact Card */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Contact Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {isAuthenticated && hasAccess && <SendCVButton agency={agency} />}
                                    {agency.email && (
                                        <a 
                                            href={hasAccess ? `mailto:${agency.email}` : '#'}
                                            className={`flex items-center gap-3 text-neutral-600 hover:text-primary-600 ${!hasAccess ? 'pointer-events-none' : ''}`}
                                        >
                                            <Mail className="w-5 h-5 text-primary-600" />
                                            <span className={!hasAccess ? 'blur-sm select-none' : 'underline'}>{agency.email}</span>
                                        </a>
                                    )}
                                    {agency.phone && (
                                        <a 
                                            href={hasAccess ? `tel:${agency.phone?.replace(/\s/g, '')}` : '#'}
                                            className={`flex items-center gap-3 text-neutral-600 hover:text-primary-600 ${!hasAccess ? 'pointer-events-none' : ''}`}
                                        >
                                            <Phone className="w-5 h-5 text-primary-600" />
                                            <span className={!hasAccess ? 'blur-sm select-none' : 'underline'}>{agency.phone}</span>
                                        </a>
                                    )}
                                    {agency.website && (
                                        <a 
                                            href={hasAccess ? agency.website : '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-3 text-neutral-600 hover:text-primary-600 ${!hasAccess ? 'pointer-events-none' : ''}`}
                                        >
                                            <Globe className="w-5 h-5 text-primary-600" />
                                            <span className={!hasAccess ? 'blur-sm select-none' : 'underline'}>{agency.website}</span>
                                        </a>
                                    )}

                                    {!hasAccess && (
                                        <div className="mt-4 p-4 bg-primary-50 rounded-lg">
                                            <p className="text-sm text-primary-800 mb-3">
                                                Create a free account to unlock full contact details
                                            </p>
                                            <Button asChild className="w-full" size="sm">
                                                <Link to="/login" data-testid="contact-signup-btn">Sign Up Free</Link>
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Operating Cities */}
                            {agency.operating_cities?.length > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Operating Locations</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex flex-wrap gap-2">
                                            <Badge variant="default">{agency.city} (HQ)</Badge>
                                            {agency.operating_cities.map((city) => (
                                                <Badge key={city} variant="secondary">{city}</Badge>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Write Review Dialog */}
            <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Write a Review</DialogTitle>
                        <DialogDescription>
                            Share your experience with {agency.name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                        {/* Rating */}
                        <div>
                            <label className="text-sm font-medium text-neutral-700 mb-2 block">Rating</label>
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                                        className="p-1 hover:scale-110 transition-transform"
                                    >
                                        <Star
                                            className={`w-8 h-8 ${
                                                star <= reviewForm.rating
                                                    ? 'text-amber-400 fill-amber-400'
                                                    : 'text-neutral-300'
                                            }`}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Title */}
                        <div>
                            <label className="text-sm font-medium text-neutral-700 mb-2 block">Review Title</label>
                            <Input
                                placeholder="Summarize your experience"
                                value={reviewForm.title}
                                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                                data-testid="review-title-input"
                            />
                        </div>

                        {/* Content */}
                        <div>
                            <label className="text-sm font-medium text-neutral-700 mb-2 block">Your Review</label>
                            <Textarea
                                placeholder="Tell others about your experience..."
                                rows={4}
                                value={reviewForm.content}
                                onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                                data-testid="review-content-textarea"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowReviewDialog(false)}>
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                disabled={submittingReview}
                                data-testid="submit-review-btn"
                            >
                                {submittingReview ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                Submit Review
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default AgencyDetailPage;
